import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';

import * as QPlayerSVGElement from '../../QPlayerSVGElement';
import { isUsedOnMobile, joinClassNames, SCREEN_TYPE } from '../../../util/util';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import { usePlayer } from '../../../contexts/PlayerContext';

import './CenteredPlayPauseOverlay.scss';

interface Props {
	settings?: any;
	playerHover?: boolean;
	isMobile?: boolean;
	isInlinePlayer?: boolean;
}

export const CenteredPlayPauseOverlay: React.FC<Props> = ({ settings, playerHover, isMobile, isInlinePlayer }) => {
	const { t } = useTranslation();
	const { isPlaying } = usePlayer();
	const { currentMediaId, playlistId, configParams, screenType } = useConfiguration();

	const [isHovered, setIsHovered] = useState(false);
	const fadeoutTimeOut = useRef<any>();

	const handleMouseEnter = () => {
		clearTimeout(fadeoutTimeOut.current);
		setIsHovered(true);
	};

	const handleMouseLeave = () => {
		fadeoutTimeOut.current = setTimeout(() => setIsHovered(false), 80);
	};

	const currentPlayerId = `player-${playlistId || currentMediaId}-${configParams.containerKey}`;

	const handlePlay = useCallback(() => {
		(window as any).GoBrain.widgets(currentPlayerId)?.play?.();
	}, [currentPlayerId]);

	const handlePause = useCallback(() => {
		(window as any).GoBrain.widgets(currentPlayerId)?.pause?.();
	}, [currentPlayerId]);

	const enabledByEmbed = configParams.centerPlayPause === 'true';
	const enabledBySetting = settings?.bottomControls?.showCenteredPlayPauseButton === true;

	if (!enabledByEmbed && !enabledBySetting) {
		return null;
	}

	// Hide when the existing CenterControls is visible to avoid two overlapping play buttons
	const centerControlsVisible =
		(screenType === SCREEN_TYPE.mobile && !isUsedOnMobile(configParams)) ||
		((isInlinePlayer || configParams?.qplayerPreview === 'true') &&
			settings?.bottomControls?.playPausePosition === 'center' &&
			(screenType === SCREEN_TYPE.mobile || screenType === SCREEN_TYPE.small || isMobile));

	if (centerControlsVisible) {
		return null;
	}

	const color = settings?.playerStyling?.primaryColor ?? '#126cfc';
	const opacity = Number(settings?.playerStyling?.primaryOpacity ?? 100);
	const bgStyle = {
		backgroundColor: tinycolor({ ...tinycolor(color).toRgb(), a: opacity / 100 }).toRgbString(),
	};

	return (
		<button
			className={joinClassNames('centeredPlayPauseOverlay', playerHover ? 'centeredPlayPauseOverlay--visible' : '')}
			style={bgStyle}
			onClick={isPlaying ? handlePause : handlePlay}
			aria-label={isPlaying ? t('PAUSE') : t('PLAY')}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={handleMouseEnter}
			onBlur={handleMouseLeave}
			onKeyDown={(e) => { if (e.key === 'Escape') { clearTimeout(fadeoutTimeOut.current); setIsHovered(false); } }}
		>
			{isPlaying ? <QPlayerSVGElement.Pause /> : <QPlayerSVGElement.Play />}
			<div
				aria-hidden="true"
				className={joinClassNames([
					'controlbar__control-tooltip',
					'controlbar__control-tooltip--light',
					'centeredPlayPauseOverlay__tooltip',
					isHovered ? 'controlbar__control-tooltip--visible' : '',
				])}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<div className="controlbar__control-tooltip-text">
					{isPlaying ? t('PAUSE') : t('PLAY')}
				</div>
			</div>
		</button>
	);
};
